import React from "react";
import { Link } from "react-router-dom";
import {
  AcademicCapIcon,
  GlobeAltIcon,
  UserGroupIcon,
  LightBulbIcon,
  CheckCircleIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";

const About: React.FC = () => {
  const values = [
    {
      icon: AcademicCapIcon,
      title: "Academic Excellence",
      description:
        "We partner with top-ranked Korean universities known for their world-class education, cutting-edge research, and innovative programs in technology, business, and sciences.",
    },
    {
      icon: GlobeAltIcon,
      title: "Global Opportunities",
      description:
        "Study abroad in South Korea opens doors to Asia-Pacific markets, Korean language proficiency, and international career opportunities in leading global companies.",
    },
    {
      icon: UserGroupIcon,
      title: "Personalized Support",
      description:
        "Our expert education consultants provide one-on-one guidance throughout your university application process, visa assistance, and scholarship opportunities.",
    },
    {
      icon: LightBulbIcon,
      title: "Innovation Focus",
      description:
        "Access Korea's thriving tech ecosystem, startup culture, and industry connections in AI, robotics, biotechnology, and digital innovation sectors.",
    },
  ];

  const achievements = [
    {
      number: "500+",
      label: "Students Placed",
      description: "Successfully admitted to Korean universities",
    },
    {
      number: "10+",
      label: "University Partners",
      description: "Top-ranked institutions across South Korea",
    },
    {
      number: "95%",
      label: "Visa Success Rate",
      description: "Student visa approval rate",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              About Raah Global
            </h1>
            <p className="text-xl md:text-2xl text-cyan-100 mb-8 leading-relaxed">
              Your trusted partner for studying abroad in South Korea. We make
              Korean university admissions accessible, affordable, and
              achievable for international students worldwide.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/universities"
                className="bg-white text-cyan-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center"
              >
                Explore Universities
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/services"
                className="bg-orange-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors"
              >
                Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  Empowering Global Education Dreams
                </h2>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  At Raah Global, we believe every student deserves access to
                  world-class education. South Korea has emerged as a leading
                  destination for international students, offering exceptional
                  universities, innovative technology programs, and rich
                  cultural experiences.
                </p>
                <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                  Our mission is to bridge the gap between ambitious students
                  and prestigious Korean universities through comprehensive
                  admission guidance, scholarship assistance, and ongoing
                  support throughout your academic journey.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" />
                    <span className="text-gray-700">
                      Expert Admission Counseling
                    </span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" />
                    <span className="text-gray-700">Scholarship Guidance</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" />
                    <span className="text-gray-700">Visa Assistance</span>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl p-8 text-white">
                  <h3 className="text-2xl font-bold mb-4">
                    Why Choose South Korea?
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-5 w-5 mr-3 mt-1 flex-shrink-0" />
                      <span>
                        Top-ranked universities globally recognized for academic
                        excellence
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-5 w-5 mr-3 mt-1 flex-shrink-0" />
                      <span>
                        Affordable tuition fees compared to Western countries
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-5 w-5 mr-3 mt-1 flex-shrink-0" />
                      <span>
                        Rich cultural heritage and modern technology hub
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-5 w-5 mr-3 mt-1 flex-shrink-0" />
                      <span>Gateway to Asia-Pacific career opportunities</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-5 w-5 mr-3 mt-1 flex-shrink-0" />
                      <span>
                        Extensive scholarship programs for international
                        students
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Our Core Values
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                What drives us to provide exceptional study abroad consulting
                services
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow"
                >
                  <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center mb-4">
                    <value.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Our Track Record
              </h2>
              <p className="text-xl text-gray-600">
                Numbers that reflect our commitment to student success
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {achievements.map((achievement, index) => (
                <div key={index} className="text-center">
                  <div className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl p-6 mb-4">
                    <div className="text-3xl md:text-4xl font-bold mb-2">
                      {achievement.number}
                    </div>
                    <div className="text-cyan-100 font-semibold">
                      {achievement.label}
                    </div>
                  </div>
                  <p className="text-gray-600">{achievement.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">
              Ready to Start Your Korean University Journey?
            </h2>
            <p className="text-xl text-cyan-100 mb-8">
              Join thousands of students who have successfully started their
              academic journey in South Korea with our expert guidance and
              support.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/universities"
                className="bg-white text-cyan-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center"
              >
                Browse Universities
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/register"
                className="bg-orange-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors"
              >
                Get Started Today
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
